import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "./supabaseClient";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Mirror of App.jsx's palette (kept local so this panel is self-contained, same
// as the other panels).
const theme = {
  bg: "#0a0f1a",
  surface: "#111827",
  surfaceHover: "#1a2234",
  border: "#1e2a3a",
  accent: "#00e5a0",
  accentDim: "rgba(0, 229, 160, 0.12)",
  text: "#e2e8f0",
  textMuted: "#64748b",
  textDim: "#475569",
  error: "#f87171",
  errorBg: "rgba(248, 113, 113, 0.08)",
  warning: "#fbbf24",
};

const mono = "'IBM Plex Mono', monospace";

// Parse one SSE frame ("event: x\ndata: {...}") into {event, data}.
function parseFrame(frame) {
  let event = "message";
  const dataLines = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  let data = {};
  try {
    data = JSON.parse(dataLines.join("\n"));
  } catch {
    /* leave data empty on malformed frame */
  }
  return { event, data };
}

function formatResetTime(iso) {
  if (!iso) return "midnight ET";
  try {
    return (
      new Date(iso).toLocaleString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/New_York",
      }) + " ET"
    );
  } catch {
    return "midnight ET";
  }
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function AgentPanel({ active, session, user, onSignInClick }) {
  const [question, setQuestion] = useState("");
  const [status, setStatus] = useState("idle"); // idle | streaming | done | error
  const [steps, setSteps] = useState([]); // assistant + tool events, in order
  const [synthesis, setSynthesis] = useState("");
  const [quota, setQuota] = useState(null); // {used, limit, remaining}
  const [errorMsg, setErrorMsg] = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  const abortRef = useRef(null);

  // Historical runs (read client-side via RLS, like query_history).
  const [pastRuns, setPastRuns] = useState([]);
  const [runsLoaded, setRunsLoaded] = useState(false);
  const [viewing, setViewing] = useState(null); // {run, artifacts, loading} or null (= live view)

  const streaming = status === "streaming";

  const fetchPastRuns = async () => {
    const { data, error } = await supabase
      .from("agent_runs")
      .select("run_id, question, status, iteration_count, final_synthesis, created_at")
      .order("created_at", { ascending: false })
      .limit(25);
    if (!error) setPastRuns(data || []);
    setRunsLoaded(true);
  };

  // Load history the first time the tab is opened by a signed-in user.
  useEffect(() => {
    if (active && user && !runsLoaded) fetchPastRuns();
  }, [active, user, runsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRun = async (run) => {
    setViewing({ run, artifacts: [], loading: true });
    const { data } = await supabase
      .from("agent_run_artifacts")
      .select("iteration, call_index, description, html")
      .eq("run_id", run.run_id)
      .order("iteration", { ascending: true })
      .order("call_index", { ascending: true });
    setViewing({ run, artifacts: data || [], loading: false });
  };

  const handleHttpError = async (res) => {
    let detail = null;
    try {
      detail = (await res.json()).detail;
    } catch {
      /* non-JSON error body */
    }
    if (res.status === 401) {
      setErrorMsg("Your session isn't valid — sign in with a verified account to use agent mode.");
    } else if (res.status === 429) {
      if (detail?.limit != null) setQuota({ used: detail.used, limit: detail.limit, remaining: 0 });
      setErrorMsg(
        `You've used all ${detail?.limit ?? ""} of today's agent runs. Resets at ${formatResetTime(detail?.resets_at)}.`
      );
    } else if (res.status === 422) {
      setErrorMsg(detail?.error || "Please ask a question about NFL statistics.");
    } else {
      setErrorMsg(detail?.error || `Server error (HTTP ${res.status}).`);
    }
    setStatus("error");
  };

  const runAgent = async () => {
    if (!question.trim() || streaming) return;

    setViewing(null); // switch from any historical view back to the live one
    setStatus("streaming");
    setSteps([]);
    setSynthesis("");
    setErrorMsg(null);
    setRunStatus(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API_URL}/agent/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ question: question.trim() }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        await handleHttpError(res);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (frame.trim()) dispatchFrame(parseFrame(frame));
        }
      }
      setStatus((s) => (s === "error" ? "error" : "done"));
      fetchPastRuns(); // the completed run now exists in agent_runs
    } catch (err) {
      if (err.name === "AbortError") {
        setStatus("idle");
      } else {
        setErrorMsg("Could not connect to the server.");
        setStatus("error");
      }
    }
  };

  const dispatchFrame = ({ event, data }) => {
    switch (event) {
      case "quota":
        setQuota({ used: data.used, limit: data.limit, remaining: data.remaining });
        break;
      case "assistant":
        setSteps((prev) => [...prev, { kind: "assistant", text: data.text, iter: data.iteration }]);
        break;
      case "tool_result":
        setSteps((prev) => [...prev, { kind: "tool", ...data }]);
        break;
      case "synthesis":
        setSynthesis(data.text || "");
        break;
      case "run_complete":
        setRunStatus(data.status);
        break;
      case "error":
        setErrorMsg(data.message || "The agent hit an error.");
        setStatus("error");
        break;
      default:
        break;
    }
  };

  const stop = () => abortRef.current?.abort();

  // --- Signed-out gate: mirror the server-side verified-user requirement. ---
  if (!user) {
    return (
      <div style={styles.gateCard}>
        <div style={{ fontSize: 15, color: theme.text, marginBottom: 8 }}>
          Agent mode is for verified accounts
        </div>
        <p style={{ fontSize: 13, color: theme.textMuted, lineHeight: 1.6, margin: "0 0 18px" }}>
          The AI analyst runs multi-step investigations to build an argument from the data. It is
          limited to signed-in, email-verified users. Sign in to run up to 3 analyses per day.
        </p>
        <button style={styles.primaryBtn} onClick={onSignInClick}>
          Sign In / Sign Up
        </button>
      </div>
    );
  }

  return (
    <div>
      <MarkdownStyles />

      {/* Input */}
      <div style={styles.inputWrap}>
        <textarea
          style={styles.textarea}
          placeholder="e.g. Is Jalen Hurts a top-5 QB going into next season?"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) runAgent();
          }}
          rows={3}
          disabled={streaming}
        />
        <div style={styles.inputFooter}>
          <span style={{ fontSize: 11, color: theme.textDim }}>
            {quota
              ? `${quota.remaining} of ${quota.limit} runs left today · each run uses one`
              : "Each analysis uses one of your limited daily runs · ⌘/Ctrl+Enter to run"}
          </span>
          {streaming ? (
            <button style={styles.stopBtn} onClick={stop}>
              Stop
            </button>
          ) : (
            <button
              style={{ ...styles.primaryBtn, opacity: question.trim() ? 1 : 0.5 }}
              onClick={runAgent}
              disabled={!question.trim()}
            >
              Run analysis
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {errorMsg && (
        <div style={styles.errorBanner}>
          {errorMsg}
          {status === "error" && errorMsg.includes("sign in") && (
            <button style={{ ...styles.linkBtn, marginLeft: 8 }} onClick={onSignInClick}>
              Sign in
            </button>
          )}
        </div>
      )}

      {/* Main area: a loaded historical run, or the live/most-recent run. */}
      {viewing ? (
        <HistoricalRunView viewing={viewing} onBack={() => setViewing(null)} />
      ) : (
        <>
          {steps.map((step, i) =>
            step.kind === "assistant" ? (
              <div key={i} style={styles.assistantStep}>
                {step.text}
              </div>
            ) : (
              <ToolStep key={i} step={step} />
            )
          )}

          {streaming && !synthesis && (
            <div style={styles.thinking}>
              <span style={styles.pulseDot} /> analyzing…
            </div>
          )}

          {synthesis && (
            <SynthesisView
              text={synthesis}
              note={
                runStatus === "max_iterations_reached"
                  ? "Note: the agent hit its step limit and summarized from the data it had gathered."
                  : null
              }
            />
          )}
        </>
      )}

      {/* Recent runs */}
      <RecentRuns
        runs={pastRuns}
        activeRunId={viewing?.run?.run_id}
        onSelect={loadRun}
      />
    </div>
  );
}

function ToolStep({ step }) {
  const isViz = step.tool_name === "generate_visualization";
  const label = isViz ? "built a visualization" : "ran a SQL query";
  return (
    <div style={styles.toolStep}>
      <div style={styles.toolHeader}>
        <span style={{ color: step.error ? theme.error : theme.accent }}>▸</span>
        <span style={{ color: theme.textMuted }}>{label}</span>
        {step.duration_ms != null && !step.error && (
          <span style={{ color: theme.textDim, fontSize: 11 }}>· {step.duration_ms}ms</span>
        )}
        {step.error && <span style={{ color: theme.error, fontSize: 11 }}>· failed (retrying)</span>}
      </div>
      {step.error && <div style={styles.toolError}>{step.error}</div>}
      {isViz && step.html && (
        <iframe
          sandbox="allow-scripts"
          srcDoc={step.html}
          style={styles.iframe}
          title={step.description || "Visualization"}
        />
      )}
    </div>
  );
}

function SynthesisView({ text, note }) {
  return (
    <div style={styles.synthesisCard}>
      <div className="agent-md">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </div>
      {note && <div style={styles.noteBanner}>{note}</div>}
    </div>
  );
}

function HistoricalRunView({ viewing, onBack }) {
  const { run, artifacts, loading } = viewing;
  return (
    <div>
      <div style={styles.viewingHeader}>
        <button style={styles.linkBtn} onClick={onBack}>
          ← back to current
        </button>
        <span style={{ color: theme.textDim, fontSize: 11 }}>
          {formatDate(run.created_at)} · {run.status}
        </span>
      </div>
      <div style={styles.viewingQuestion}>{run.question}</div>

      {/* Stored charts (the full-HTML artifacts). */}
      {artifacts.map((a, i) => (
        <div key={i} style={styles.toolStep}>
          {a.description && (
            <div style={{ ...styles.toolHeader, color: theme.textMuted }}>{a.description}</div>
          )}
          {a.html && (
            <iframe
              sandbox="allow-scripts"
              srcDoc={a.html}
              style={styles.iframe}
              title={a.description || "Visualization"}
            />
          )}
        </div>
      ))}

      {loading ? (
        <div style={styles.thinking}>
          <span style={styles.pulseDot} /> loading run…
        </div>
      ) : (
        run.final_synthesis && <SynthesisView text={run.final_synthesis} />
      )}
    </div>
  );
}

function RecentRuns({ runs, activeRunId, onSelect }) {
  if (!runs.length) return null;
  return (
    <div style={{ marginTop: 28 }}>
      <div style={styles.recentHeader}>Recent runs</div>
      {runs.map((r) => (
        <button
          key={r.run_id}
          onClick={() => onSelect(r)}
          style={{
            ...styles.recentItem,
            ...(r.run_id === activeRunId ? styles.recentItemActive : {}),
          }}
        >
          <span style={styles.recentQuestion}>{r.question}</span>
          <span style={styles.recentMeta}>{formatDate(r.created_at)}</span>
        </button>
      ))}
    </div>
  );
}

// Scoped styling for the rendered markdown synthesis (tables, headings, etc.).
function MarkdownStyles() {
  return (
    <style>{`
      .agent-md { color: ${theme.text}; font-size: 14px; line-height: 1.7; }
      .agent-md h1, .agent-md h2, .agent-md h3 { color: ${theme.text}; margin: 18px 0 10px; line-height: 1.3; }
      .agent-md h1 { font-size: 20px; } .agent-md h2 { font-size: 17px; } .agent-md h3 { font-size: 15px; }
      .agent-md p { margin: 10px 0; }
      .agent-md strong { color: ${theme.accent}; }
      .agent-md a { color: ${theme.accent}; }
      .agent-md ul, .agent-md ol { padding-left: 22px; margin: 10px 0; }
      .agent-md li { margin: 4px 0; }
      .agent-md blockquote { border-left: 3px solid ${theme.border}; margin: 12px 0; padding: 4px 14px; color: ${theme.textMuted}; }
      .agent-md code { background: ${theme.surfaceHover}; padding: 1px 5px; border-radius: 4px; font-size: 12px; }
      .agent-md table { border-collapse: collapse; width: 100%; margin: 14px 0; display: block; overflow-x: auto; font-size: 13px; }
      .agent-md th, .agent-md td { border: 1px solid ${theme.border}; padding: 7px 10px; text-align: left; }
      .agent-md th { background: ${theme.surfaceHover}; color: ${theme.text}; }
      .agent-md hr { border: none; border-top: 1px solid ${theme.border}; margin: 18px 0; }
      @keyframes agentPulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
    `}</style>
  );
}

const styles = {
  gateCard: {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: "32px 28px",
    textAlign: "center",
    maxWidth: 460,
    margin: "20px auto",
    fontFamily: mono,
  },
  inputWrap: {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  textarea: {
    width: "100%",
    background: theme.bg,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    color: theme.text,
    fontFamily: mono,
    fontSize: 14,
    padding: 12,
    resize: "vertical",
    boxSizing: "border-box",
    outline: "none",
  },
  inputFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 12,
    gap: 12,
  },
  primaryBtn: {
    background: theme.accentDim,
    border: `1px solid ${theme.accent}55`,
    borderRadius: 8,
    padding: "8px 18px",
    color: theme.accent,
    fontSize: 13,
    fontFamily: mono,
    cursor: "pointer",
  },
  stopBtn: {
    background: theme.errorBg,
    border: `1px solid ${theme.error}55`,
    borderRadius: 8,
    padding: "8px 18px",
    color: theme.error,
    fontSize: 13,
    fontFamily: mono,
    cursor: "pointer",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: theme.accent,
    fontFamily: mono,
    fontSize: 13,
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
  },
  errorBanner: {
    background: theme.errorBg,
    border: `1px solid ${theme.error}44`,
    borderRadius: 8,
    padding: "12px 16px",
    color: theme.error,
    fontSize: 13,
    fontFamily: mono,
    marginBottom: 16,
  },
  assistantStep: {
    color: theme.textMuted,
    fontSize: 13,
    fontStyle: "italic",
    fontFamily: mono,
    lineHeight: 1.6,
    padding: "8px 0",
  },
  toolStep: {
    borderLeft: `2px solid ${theme.border}`,
    paddingLeft: 14,
    margin: "10px 0",
  },
  toolHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontFamily: mono,
  },
  toolError: {
    color: theme.error,
    fontSize: 11,
    fontFamily: mono,
    marginTop: 4,
    opacity: 0.8,
  },
  iframe: {
    width: "100%",
    height: 450,
    border: "none",
    borderRadius: 8,
    background: theme.surfaceHover,
    display: "block",
    marginTop: 12,
  },
  thinking: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: theme.textMuted,
    fontSize: 13,
    fontFamily: mono,
    padding: "12px 0",
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: theme.accent,
    display: "inline-block",
    animation: "agentPulse 1.2s ease-in-out infinite",
  },
  synthesisCard: {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: "20px 24px",
    marginTop: 16,
  },
  noteBanner: {
    marginTop: 16,
    padding: "10px 14px",
    background: "rgba(251, 191, 36, 0.08)",
    border: `1px solid ${theme.warning}44`,
    borderRadius: 8,
    color: theme.warning,
    fontSize: 12,
    fontFamily: mono,
  },
  viewingHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  viewingQuestion: {
    fontSize: 15,
    color: theme.text,
    fontFamily: mono,
    marginBottom: 14,
    paddingBottom: 12,
    borderBottom: `1px solid ${theme.border}`,
  },
  recentHeader: {
    fontSize: 12,
    color: theme.textMuted,
    fontFamily: mono,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 10,
    paddingTop: 20,
    borderTop: `1px solid ${theme.border}`,
  },
  recentItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    width: "100%",
    textAlign: "left",
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 6,
    color: theme.text,
    fontFamily: mono,
    fontSize: 13,
    cursor: "pointer",
  },
  recentItemActive: {
    borderColor: `${theme.accent}55`,
    background: theme.accentDim,
  },
  recentQuestion: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  recentMeta: {
    color: theme.textDim,
    fontSize: 11,
    flexShrink: 0,
  },
};
